/**
 * Bank Account Service
 *
 * API client for bank account management.
 */

import {
  get,
  post,
  put,
  del,
  buildQueryString,
  type ApiResponse,
} from './api-client';

// ============================================================================
// Types
// ============================================================================

/**
 * Bank account types.
 */
export type BankAccountType =
  | 'current'   // Current/Checking account
  | 'savings'   // Savings account
  | 'od'        // Overdraft account
  | 'cc'        // Credit card
  | 'fd'        // Fixed deposit
  | 'cash';     // Cash account

/**
 * Bank account entity.
 */
export interface BankAccount {
  id: string;
  orgId: string;
  ledgerId: string;

  // Account identification
  accountName: string;
  nameAtBank: string;
  accountType: BankAccountType;

  // Bank details
  bankName: string;
  branch: string;
  accountNumber: string;
  maskedAccountNumber: string; // Masked for security (e.g., "******7890")

  // Bank identifiers (region-specific)
  ifscCode: string;       // Indian Financial System Code
  swiftCode: string;      // SWIFT/BIC code for international transfers
  iban: string;           // International Bank Account Number
  routingNumber: string;  // ABA routing number (US)
  sortCode: string;       // Sort code (UK)

  // Currency and accounting
  currency: string;
  accountId: string;      // Links to Chart of Accounts

  // Settings
  isDefault: boolean;
  isActive: boolean;
  displayOrder: number;

  // Additional details
  notes: string;

  // Audit fields
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
}

/**
 * Request to create a new bank account.
 */
export interface CreateBankAccountRequest {
  ledgerId: string;
  accountName: string;
  nameAtBank?: string;
  accountType: BankAccountType;
  bankName?: string;
  branch?: string;
  accountNumber?: string;
  ifscCode?: string;
  swiftCode?: string;
  iban?: string;
  routingNumber?: string;
  sortCode?: string;
  currency: string;
  accountId?: string;
  isDefault?: boolean;
  displayOrder?: number;
  notes?: string;
  // Frontend-only: used to create opening balance journal entry after account creation
  openingBalance?: number;
  openingDate?: string;
}

/**
 * Request to update an existing bank account.
 */
export interface UpdateBankAccountRequest {
  accountName?: string;
  nameAtBank?: string;
  accountType?: BankAccountType;
  bankName?: string;
  branch?: string;
  accountNumber?: string;
  ifscCode?: string;
  swiftCode?: string;
  iban?: string;
  routingNumber?: string;
  sortCode?: string;
  currency?: string;
  accountId?: string;
  isActive?: boolean;
  displayOrder?: number;
  notes?: string;
}

/**
 * Response when listing bank accounts.
 */
export interface ListBankAccountsResponse {
  bankAccounts: BankAccount[];
  totalCount: number;
}

/**
 * Filters for listing bank accounts.
 */
export interface ListBankAccountsFilters {
  ledgerId: string;
  activeOnly?: boolean;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * List all bank accounts for an organization/ledger.
 */
export async function listBankAccounts(
  orgId: string,
  filters: ListBankAccountsFilters
): Promise<ApiResponse<ListBankAccountsResponse>> {
  const query = buildQueryString({
    ledgerId: filters.ledgerId,
    activeOnly: filters.activeOnly ?? true,
  });

  return get<ListBankAccountsResponse>(
    `/organizations/${orgId}/bank-accounts${query}`
  );
}

/**
 * Get a specific bank account by ID.
 */
export async function getBankAccount(
  orgId: string,
  bankAccountId: string
): Promise<ApiResponse<BankAccount>> {
  return get<BankAccount>(
    `/organizations/${orgId}/bank-accounts/${bankAccountId}`
  );
}

/**
 * Get the default bank account for a ledger.
 */
export async function getDefaultBankAccount(
  orgId: string,
  ledgerId: string
): Promise<ApiResponse<BankAccount>> {
  const accounts = await listBankAccounts(orgId, { ledgerId, activeOnly: true });

  if (accounts.error) {
    return { error: accounts.error };
  }

  const defaultAccount = accounts.data?.bankAccounts.find(
    (acc) => acc.isDefault && acc.isActive
  );

  if (!defaultAccount) {
    return {
      error: {
        code: 'NOT_FOUND',
        message: 'No default bank account found',
      },
    };
  }

  return { data: defaultAccount };
}

/**
 * Create a new bank account.
 */
export async function createBankAccount(
  orgId: string,
  data: CreateBankAccountRequest
): Promise<ApiResponse<BankAccount>> {
  return post<BankAccount>(
    `/organizations/${orgId}/bank-accounts`,
    data
  );
}

/**
 * Update an existing bank account.
 */
export async function updateBankAccount(
  orgId: string,
  bankAccountId: string,
  data: UpdateBankAccountRequest
): Promise<ApiResponse<BankAccount>> {
  return put<BankAccount>(
    `/organizations/${orgId}/bank-accounts/${bankAccountId}`,
    data
  );
}

/**
 * Set a bank account as the default for a ledger.
 * This will unset any other default bank accounts for the same org/ledger.
 */
export async function setDefaultBankAccount(
  orgId: string,
  ledgerId: string,
  bankAccountId: string
): Promise<ApiResponse<{ message: string }>> {
  const query = buildQueryString({ ledgerId });

  return put<{ message: string }>(
    `/organizations/${orgId}/bank-accounts/${bankAccountId}/set-default${query}`
  );
}

/**
 * Delete a bank account (soft delete - marks as inactive).
 */
export async function deleteBankAccount(
  orgId: string,
  bankAccountId: string
): Promise<ApiResponse<void>> {
  return del<void>(
    `/organizations/${orgId}/bank-accounts/${bankAccountId}`
  );
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Format account number for display (masked).
 * Example: "1234567890" becomes "******7890"
 */
export function maskAccountNumber(accountNumber: string): string {
  if (!accountNumber || accountNumber.length <= 4) {
    return accountNumber;
  }

  const visibleDigits = 4;
  const maskedLength = accountNumber.length - visibleDigits;
  return '*'.repeat(maskedLength) + accountNumber.slice(-visibleDigits);
}

/**
 * Get display label for bank account type.
 */
export function getBankAccountTypeLabel(type: BankAccountType): string {
  const labels: Record<BankAccountType, string> = {
    current: 'Current Account',
    savings: 'Savings Account',
    od: 'Overdraft Account',
    cc: 'Credit Card',
    fd: 'Fixed Deposit',
    cash: 'Cash Account',
  };
  /* v8 ignore next */
  return labels[type] || type;
}

/**
 * Validate IFSC code format (India).
 * Format: ABCD0123456 (4 alpha, 7 numeric)
 */
export function isValidIFSC(ifscCode: string): boolean {
  if (!ifscCode) {return true;} // Optional field
  const pattern = /^[A-Z]{4}0[A-Z0-9]{6}$/;
  return pattern.test(ifscCode);
}

/**
 * Validate SWIFT/BIC code format.
 * Format: 8 or 11 characters (AAAABBCCDDD)
 */
export function isValidSWIFT(swiftCode: string): boolean {
  if (!swiftCode) {return true;} // Optional field
  const pattern = /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/;
  return pattern.test(swiftCode);
}

/**
 * Validate IBAN format.
 * Basic validation - 15 to 34 alphanumeric characters
 */
export function isValidIBAN(iban: string): boolean {
  if (!iban) {return true;} // Optional field
  const pattern = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/;
  return pattern.test(iban.replace(/\s/g, ''));
}
